/**
 * ZIP Code Formatter
 * 
 * Handles formatting of US ZIP codes in both 5-digit (XXXXX) and ZIP+4 (XXXXX-XXXX) formats.
 * Designed to be future-ready for Google Places API integration while maintaining simplicity.
 */

import { BaseFormatter, FormatContext, ExtendedFormattingResult } from './base-formatter';
import { FormattingOptions, ValidationResult } from '../types/formatting-types';

/**
 * ZIP code format types
 */
export enum ZipFormatType {
  ZIP5 = 'zip5',       // 5-digit ZIP code (primary)
  ZIP4 = 'zip4'        // ZIP+4 format (future support)
}

/**
 * ZIP code formatter supporting 5-digit and ZIP+4 formats
 * 
 * Features:
 * - Primary: 5-digit ZIP code formatting (XXXXX)
 * - Future-ready: ZIP+4 support (XXXXX-XXXX) for Google API
 * - Progressive formatting during typing
 * - Input validation (numeric only for base ZIP)
 * - Automatic format detection
 */
export class ZipFormatter extends BaseFormatter {
  private readonly maxZip5Length = 5;
  private readonly maxZip4Length = 9; // 5 + 4 digits

  constructor() {
    super('zip', {
      // Future: Support ZIP+4 format
      // Currently focused on 5-digit ZIP for simplicity
    });
  }

  /**
   * Extract raw digits from formatted ZIP code
   */
  public extractRaw(formattedValue: string): string {
    return this.stripFormatting(formattedValue);
  }

  /**
   * Check if this formatter handles ZIP code fields
   */
  public canHandle(fieldType: string): boolean {
    return fieldType === 'zip' || 
           fieldType === 'zipcode' || 
           fieldType === 'postal' ||
           fieldType === 'postal_code' ||
           fieldType === 'address_zip';
  }

  /**
   * Get the expected format pattern
   */
  public getFormatPattern(): string {
    return 'XXXXX'; // Primary format, ZIP+4 future enhancement
  }

  /**
   * Sanitize input to only allow numeric characters (and dash for ZIP+4)
   */
  protected sanitizeInput(value: string): string {
    // For now, only allow numeric characters for 5-digit ZIP
    // Future: Allow dash for ZIP+4 format
    return value.replace(/\D/g, '');
  }

  /**
   * Perform progressive ZIP code formatting
   */
  protected performFormat(context: FormatContext): ExtendedFormattingResult {
    const { rawValue, cursorPosition, isProgressive } = context;
    
    // Determine format type based on length
    const formatType = this.determineFormatType(rawValue);
    
    let formattedValue = '';
    let processedDigits = rawValue;
    
    if (formatType === ZipFormatType.ZIP5) {
      // 5-digit ZIP code formatting
      processedDigits = rawValue.substring(0, this.maxZip5Length);
      formattedValue = processedDigits;
      
    } else if (formatType === ZipFormatType.ZIP4) {
      // ZIP+4 formatting (future enhancement)
      // Format: XXXXX-XXXX
      processedDigits = rawValue.substring(0, this.maxZip4Length);
      
      if (processedDigits.length <= 5) {
        formattedValue = processedDigits;
      } else {
        const zip5 = processedDigits.substring(0, 5);
        const zip4 = processedDigits.substring(5);
        formattedValue = `${zip5}-${zip4}`;
      }
    }

    // Calculate cursor position for progressive formatting
    let newCursorPosition = cursorPosition;
    if (isProgressive && cursorPosition !== undefined) {
      newCursorPosition = this.calculateZipCursorPosition(
        rawValue,
        processedDigits,
        formattedValue,
        cursorPosition
      );
    }

    return {
      formattedValue,
      rawValue: processedDigits,
      success: true,
      errors: [],
      newCursorPosition,
      wasModified: rawValue !== formattedValue
    };
  }

  /**
   * Validate ZIP code
   */
  protected performValidation(rawValue: string, options?: FormattingOptions): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check minimum length (5 digits required)
    if (rawValue.length < 5) {
      errors.push('ZIP code must be at least 5 digits');
    }

    // Check for valid length (5 or 9 digits)
    if (rawValue.length !== 5 && rawValue.length !== 9) {
      errors.push('ZIP code must be 5 or 9 digits');
    }

    // Basic format validation
    if (rawValue.length === 5) {
      // All digits should be numeric (already enforced by sanitization)
      if (!/^\d{5}$/.test(rawValue)) {
        errors.push('ZIP code must contain only numbers');
      }

      // Basic US ZIP code validation (first digit 0-9, but some ranges are more common)
      const firstDigit = rawValue.charAt(0);
      if (firstDigit < '0' || firstDigit > '9') {
        errors.push('Invalid ZIP code format');
      }
    }

    // ZIP+4 validation (future enhancement)
    if (rawValue.length === 9) {
      if (!/^\d{9}$/.test(rawValue)) {
        errors.push('ZIP+4 code must contain only numbers');
      }
    }

    // Check for obviously invalid ZIP codes
    const invalidZips = [
      '00000', '11111', '22222', '33333', '44444',
      '55555', '66666', '77777', '88888', '99999'
    ];

    if (rawValue.length === 5 && invalidZips.includes(rawValue)) {
      warnings.push('ZIP code appears to be invalid');
    }

    return { isValid: errors.length === 0, errors, warnings };
  }

  /**
   * Check if input is a complete ZIP code
   */
  protected isCompleteInput(rawValue: string): boolean {
    // Complete if 5 digits (primary format) or 9 digits (ZIP+4)
    return rawValue.length === 5 || rawValue.length === 9;
  }

  /**
   * Check if character is valid for ZIP input
   */
  protected isValidInputCharacter(char: string): boolean {
    // Currently only numeric characters
    // Future: Allow dash for ZIP+4
    return /\d/.test(char);
  }

  /**
   * Remove formatting to get raw digits
   */
  protected stripFormatting(value: string): string {
    // Remove dashes and any non-numeric characters
    return value.replace(/\D/g, '');
  }

  /**
   * Determine ZIP format type based on length
   */
  private determineFormatType(rawValue: string): ZipFormatType {
    // Future enhancement: Detect ZIP+4 format
    // For now, default to ZIP5
    return rawValue.length > 5 ? ZipFormatType.ZIP4 : ZipFormatType.ZIP5;
  }

  /**
   * Calculate cursor position after ZIP formatting
   */
  private calculateZipCursorPosition(
    originalRaw: string,
    newRaw: string,
    formatted: string,
    originalCursor: number
  ): number {
    // For ZIP codes, cursor positioning is straightforward
    // since we mainly deal with digit insertion/removal
    
    if (originalRaw.length === newRaw.length) {
      // No length change, maintain relative position
      return Math.min(originalCursor, formatted.length);
    }

    // Default: place cursor at end
    return formatted.length;
  }

  /**
   * Format ZIP code for display purposes
   * 
   * @param zipCode Raw ZIP code digits
   * @returns Formatted ZIP code string
   */
  public static formatZipForDisplay(zipCode: string): string {
    const formatter = new ZipFormatter();
    const result = formatter.format(zipCode, {}, undefined, false);
    return result.formattedValue;
  }

  /**
   * Check if a string looks like a ZIP code
   * 
   * @param value String to check
   * @returns Whether it looks like a ZIP code
   */
  public static looksLikeZipCode(value: string): boolean {
    const digits = value.replace(/\D/g, '');
    return digits.length >= 5 && digits.length <= 9;
  }

  /**
   * Parse ZIP code from various formats
   * 
   * @param input ZIP code in any format
   * @returns Clean digit string
   */
  public static parseZipCode(input: string): string {
    // Remove spaces, dashes, and any non-numeric characters
    return input.replace(/\D/g, '');
  }

  /**
   * Validate ZIP code format without formatting
   * 
   * @param zipCode Raw ZIP code to validate
   * @returns Whether the ZIP code is valid
   */
  public static isValidZipCode(zipCode: string): boolean {
    const digits = zipCode.replace(/\D/g, '');
    
    // Must be 5 or 9 digits
    if (digits.length !== 5 && digits.length !== 9) {
      return false;
    }

    // Check for invalid patterns
    const invalidZips = [
      '00000', '11111', '22222', '33333', '44444',
      '55555', '66666', '77777', '88888', '99999'
    ];

    if (digits.length === 5 && invalidZips.includes(digits)) {
      return false;
    }

    return true;
  }

  /**
   * Future: Support for ZIP+4 format detection and formatting
   * This will be enhanced when integrating with Google Places API
   */
  private formatZip4(digits: string): string {
    if (digits.length <= 5) {
      return digits;
    }
    
    const zip5 = digits.substring(0, 5);
    const zip4 = digits.substring(5, 9);
    return `${zip5}-${zip4}`;
  }
}